/**
 * Login layout script class
 *
 * @package    Containers
 * @author     Vadym Karpenko <vkarpenko@mindk.com>
 * @copyright  Copyright (c) 2010-2018 MindK (http://mindk.com). All rights reserved.
 * @license    http://mindk.com
 * @link       http://mindk.com
 */

import React, { Component } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { withStyles } from '@material-ui/core/styles';
import styles from './styles.jsx';
import { login, loginSocial, registerSocial } from 'Actions/auth';
import GridContainer from 'Common/Grid/GridContainer';
import ItemGrid from 'Common/Grid/ItemGrid';
import LoginCard from 'Common/Cards/LoginCard';
import RegisterCard from 'Common/Cards/RegisterCard';
import cx from 'classnames';

import { pageTitleDecorator, guestPageDecorator } from 'Decorators';
import BACKGROUND_IMAGE from 'Layouts/Layout/images/fullpage-image.jpeg';
import LoginForm from './LoginForm';

import { toastr } from 'react-redux-toastr';

import SocialButtonOrcid from 'Containers/SocialButtons/SocialButtonOrcid';
import Tabs from 'Components/Tabs/Tabs';
import Card from '@material-ui/core/Card/Card';
import { push } from 'react-router-redux';

@withStyles(styles)
@connect(
    store => ({
        social: store.general.social,
        loading: store.loginSocial.loading,
        loginData: store.login.data,
    }),
    dispatch => ({
        login: (data, reviewer = null) => dispatch(login(data, reviewer)),
        loginSocial: data => dispatch(loginSocial(data)),
        registerSocial: data => dispatch(registerSocial(data)),
        redirectToRegister: () => dispatch(push('/register')),
    })
)
@guestPageDecorator()
@pageTitleDecorator('login')
export default class Login extends Component {
    /**
     * @type {Object}
     */
    static propTypes = {
        social: PropTypes.object.isRequired,
        classes: PropTypes.object.isRequired,
        loading: PropTypes.bool,
        loginData: PropTypes.object,
        login: PropTypes.func.isRequired,
        loginSocial: PropTypes.func.isRequired,
        registerSocial: PropTypes.func.isRequired,
        redirectToRegister: PropTypes.func.isRequired,
    };

    /**
     * @type {Object}
     */
    static contextTypes = {
        intl: PropTypes.object.isRequired,
    };

    state = {
        activeTab: 'login'
    };

    tabs = [
        {
            name: 'login',
            text: 'login.tab.login',
        },
        {
            name: 'register',
            text: 'login.tab.register',
        },
    ];

    renderTab = () => {
        const { activeTab } = this.state;
        const {
            login,
            social: { orcid },
            loading,
            loginData: { email, reviewer },
            redirectToRegister
        } = this.props;

        switch (activeTab) {
            case 'login':
                return <LoginCard
                    cardSubtitle="If you've already registered in the system and filled ORCID in the profile, please use this button to log in:"
                    customCardClass={loading ? 'mm-hidden' : ''}
                    socials={
                        <SocialButtonOrcid
                            config={orcid}
                            onSuccess={this.onOrcidSuccess}
                            onFailure={this.onFailure}
                            title="Log in via ORCID"
                        />
                    }
                    content={<LoginForm initialValues={{ email }} className="top-login" onSubmit={(data) => login(data, reviewer)} />}
                    classes={{cardSubtitle: 'login-card-subtitle'}}
                />;
            case 'register':
                return <RegisterCard
                    cardSubtitle="If you have an ORCID account, use this button to fill in the registration form from your ORCID profile:"
                    cardWelcomeSubtitle="New user? Please register an account."
                    customCardClass={loading ? 'mm-hidden' : ''}
                    socials={
                        <SocialButtonOrcid
                            config={orcid}
                            onSuccess={this.onOrcidRegisterSuccess}
                            onFailure={this.onFailure}
                            title="register via ORCID"
                            redirectUri={window.location.href.replace('login', 'register')}
                        />
                    }
                    classes={{cardSubtitle: 'register-card-subtitle'}}
                    handleRegister={redirectToRegister}
                />;
        }
    };

    handleClick = activeTab => this.setState({activeTab});

    /**
     * Success handler
     *
     * @param data
     */
    onOrcidSuccess = data => {
        const { loginSocial } = this.props;

        if (data) {
            loginSocial({ ...data, social: 'orcid' });
        } else {
            this.onFailure();
        }
    };

    /**
     * Success handler
     *
     * @param data
     */
    onOrcidRegisterSuccess = data => {
        const { registerSocial } = this.props;

        if (data) {
            registerSocial({ ...data, social: 'orcid' });
        } else {
            this.onFailure();
        }
    };

    /**
     * Failure handler
     */
    onFailure = () => {
        setTimeout(() => toastr.error(this.context.intl.formatMessage({ id: 'alert.orcid_denied' }), { timeOut: 20000 }));
    };

    render() {
        const { classes, loading } = this.props;
        const { activeTab } = this.state;
        const customCardClass = loading ? 'mm-hidden' : '';
        const plainCardClasses = cx({
            [' ' + customCardClass]: customCardClass !== undefined,
        });

        return (
            <div className={classes.wrapper}>
                <div className={classes.fullPage}>
                    <div className={classes.content + ' vetical-centered-forms'}>
                        <div className="outside-logo outside-centered-logo login-page-logo">
                            <a href="/">
                                <div className="outside-logo-image"></div>
                                <div className="outside-logo-text"></div>
                            </a>
                        </div>
                        <div className={classes.container}>
                            <GridContainer justify="center">
                                <ItemGrid xs={12} sm={6} md={4}>
                                    <Card className={ classes.card + plainCardClasses + ' login-tabs-block-wrapper'}>
                                        <Tabs
                                            tabs={ this.tabs }
                                            active={ activeTab }
                                            handleClick={ this.handleClick }
                                            classes={{wrapper: 'mm-tabs-login-wrapper'}}
                                            isSingleDiv
                                        />
                                        { this.renderTab() }
                                    </Card>
                                </ItemGrid>
                            </GridContainer>
                        </div>
                    </div>
                    <div
                        className={classes.fullPageBackground}
                        style={{ backgroundImage: 'url(' + BACKGROUND_IMAGE + ')' }}
                    />
                </div>
            </div>
        );
    }
}



// WEBPACK FOOTER //
// ./src/layouts/Login/Login.jsx